/**
 * 鱼缸选择 Modal 组件
 * 用于在评估页面强制用户选择鱼缸
 *
 * @see Requirements 1.1, 1.2, 1.3, 5.1, 5.4
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Droplets, Ruler, Filter } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";

import tankService, { Tank } from "@/services/tankService";
import TankCreationForm from "./TankCreationForm";

export interface TankSelectionModalProps {
  /** Modal 是否打开 */
  open: boolean;
  /** 选择鱼缸回调 */
  onSelect: (tank: Tank) => void;
  /** 跳过选择回调（仅首次选择时显示） */
  onSkip?: () => void;
  /** 关闭回调 */
  onClose?: () => void;
  /** 是否为更换鱼缸模式（不显示跳过按钮） */
  isChangeMode?: boolean;
}

/**
 * 鱼缸选择 Modal 组件
 * @see Requirements 1.2 - 用户可选打开 Modal
 * @see Requirements 1.3 - 网格布局展示鱼缸
 * @see Requirements 1.4 - 点击选择鱼缸
 * @see Requirements 1.7 - 跳过选择
 * @see Requirements 5.1 - 无鱼缸时显示创建表单
 * @see Requirements 5.4 - 有鱼缸时提供创建按钮
 * @see Requirements 5.6 - 跳过创建
 */
const TankSelectionModal: React.FC<TankSelectionModalProps> = ({
  open,
  onSelect,
  onSkip,
  onClose,
  isChangeMode = false,
}) => {
  const { t } = useTranslation("assessments");
  const queryClient = useQueryClient();

  // 是否显示创建表单
  const [showCreateForm, setShowCreateForm] = useState(false);

  // 获取用户鱼缸列表
  const {
    data: tanksData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tanks"],
    queryFn: () => tankService.getTanks(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const tanks = tanksData?.tanks || [];
  const filterSystemOptions = tanksData?.filter_system_options || [];
  const waterTypeOptions = tanksData?.water_type_options || [];

  // 无鱼缸时自动显示创建表单
  useEffect(() => {
    if (!isLoading && tanks.length === 0) {
      setShowCreateForm(true);
    }
  }, [isLoading, tanks.length]);

  // Modal 关闭时重置状态
  useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
    }
  }, [open]);

  /**
   * 处理鱼缸选择
   * @see Requirements 1.3
   */
  const handleTankSelect = (tank: Tank) => {
    onSelect(tank);
  };

  /**
   * 处理创建成功
   * @see Requirements 5.3, 5.5
   */
  const handleCreateSuccess = (tank: Tank) => {
    // 刷新鱼缸列表缓存
    queryClient.invalidateQueries({ queryKey: ["tanks"] });
    // 自动选择新创建的鱼缸
    onSelect(tank);
  };

  /**
   * 处理跳过选择
   * @see Requirements 1.7, 5.6
   */
  const handleSkip = () => {
    onSkip?.();
    onClose?.();
  };

  /**
   * 切换到创建表单
   * @see Requirements 5.4
   */
  const handleShowCreateForm = () => {
    setShowCreateForm(true);
  };

  /**
   * 返回鱼缸列表
   */
  const handleBackToList = () => {
    setShowCreateForm(false);
  };

  // 渲染加载状态
  const renderLoading = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, idx) => (
        <Card key={idx} className="overflow-hidden">
          <CardContent className="p-4">
            <Skeleton className="h-5 w-24 mb-2" />
            <Skeleton className="h-4 w-32 mb-1" />
            <Skeleton className="h-4 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // 渲染错误状态
  const renderError = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="p-4 bg-destructive/10 rounded-lg text-sm text-destructive mb-4">
        {t("tankSelector.loadError", "加载鱼缸列表失败")}
      </div>
      <Button variant="outline" onClick={() => refetch()}>
        {t("tankSelection.retry", "重试")}
      </Button>
    </div>
  );

  // 渲染鱼缸卡片
  const renderTankCard = (tank: Tank) => (
    <Card
      key={tank.id}
      className="overflow-hidden cursor-pointer transition-all hover:border-primary hover:shadow-md"
      onClick={() => handleTankSelect(tank)}
    >
      <CardContent className="p-4">
        {/* 鱼缸名称 */}
        <div className="flex items-center gap-2 mb-2">
          <Droplets className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm truncate">{tank.name}</h4>
        </div>

        {/* 尺寸信息 */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <Ruler className="h-3 w-3" />
          <span>{tank.dimensions}</span>
          <span className="text-muted-foreground/50">·</span>
          <span>{tank.effective_volume}L</span>
        </div>

        {/* 水体类型和过滤系统 */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge variant="secondary" className="text-xs">
            {tank.water_type_text}
          </Badge>
          {tank.filter_system_text && (
            <Badge variant="outline" className="text-xs">
              <Filter className="h-3 w-3 mr-1" />
              {tank.filter_system_text}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // 渲染鱼缸列表
  const renderTankList = () => (
    <div className="space-y-4">
      {/* 创建新鱼缸按钮 */}
      {tanks.length > 0 && (
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={handleShowCreateForm}
        >
          <Plus className="h-4 w-4" />
          {t("tankSelection.createNew", "创建新鱼缸")}
        </Button>
      )}

      {/* 鱼缸网格 */}
      <ScrollArea className="max-h-[400px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pr-4">
          {tanks.map(renderTankCard)}
        </div>
      </ScrollArea>

      {/* 跳过按钮 - 仅在首次选择时显示，更换模式不显示 */}
      {onSkip && !isChangeMode && (
        <div className="pt-2 border-t">
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={handleSkip}
          >
            {t("tankSelection.skip", "跳过，直接开始评估")}
          </Button>
        </div>
      )}
    </div>
  );

  // 渲染创建表单
  const renderCreateForm = () => (
    <TankCreationForm
      onSuccess={handleCreateSuccess}
      onCancel={tanks.length > 0 ? handleBackToList : undefined}
      onSkip={onSkip && !isChangeMode ? handleSkip : undefined}
      filterSystemOptions={filterSystemOptions}
      waterTypeOptions={waterTypeOptions}
    />
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && (onClose?.() || onSkip?.())}
    >
      <DialogContent
        className="sm:max-w-lg"
        showCloseButton={!!(onClose || onSkip)}
        onPointerDownOutside={(e) => {
          // 如果没有 onClose 和 onSkip，阻止点击外部关闭
          if (!onClose && !onSkip) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // 如果没有 onClose 和 onSkip，阻止 ESC 关闭
          if (!onClose && !onSkip) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Droplets className="h-5 w-5 text-primary" />
            {showCreateForm
              ? t("tankSelection.createTitle", "创建鱼缸")
              : t("tankSelection.title", "选择鱼缸")}
          </DialogTitle>
          <DialogDescription>
            {showCreateForm
              ? t(
                  "tankSelection.createDescription",
                  "创建一个鱼缸，让 AI 更好地了解您的养殖环境",
                )
              : t(
                  "tankSelection.description",
                  "选择一个鱼缸，AI 将根据鱼缸环境提供更精准的评估建议",
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {isLoading && renderLoading()}
          {error && renderError()}
          {!isLoading &&
            !error &&
            (showCreateForm ? renderCreateForm() : renderTankList())}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TankSelectionModal;
